import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Chip, Tooltip, IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InvButton from '../InvButton';
import { getRfqPortalLink, updateRfq } from '../../api/rfq';
import { toRfqUpdatePayload } from '../../utils/rfqEntity';
import { withOpenBidQuery, withItemHintsQuery } from '../../../vendor-portal/preview';
import { formatDateTime } from '../../utils';
import { IPD_COLORS } from '../../theme';

/**
 * The shareable public link for an RFQ.
 *
 * Only meaningful once the RFQ is published — an unpublished RFQ has nothing
 * for a vendor to look at, so the link is not offered before then.
 */
export default function RfqPortalLinkCard({
  rfqId, published, rfq, items, canToggleOpenBid, onUpdated,
}) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLink(await getRfqPortalLink(rfqId));
    } catch (e) {
      setError(e.message || 'Could not build the vendor link');
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    if (published) load();
  }, [published, load]);

  const rawUrl = link
    ? (link.url || (link.token && window.location.origin + '/vendor-portal/' + link.token))
    : null;
  const openBid = Boolean(rfq?.is_open_bid) || Boolean(link?.is_open_bid);
  const shareUrl = withItemHintsQuery(withOpenBidQuery(rawUrl, openBid), items);

  async function copy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the link and copy it manually.');
    }
  }

  async function setOpenBid(next) {
    setSaving(true);
    setError(null);
    try {
      const body = rfq
        ? toRfqUpdatePayload({ ...rfq, is_open_bid: next }, {
          vendorIds: rfq.invited_vendor_ids,
          existingPayload: rfq.rfq_payload || {},
        })
        : { is_open_bid: next };
      await updateRfq(rfqId, body);
      await load();
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Could not update who is allowed to bid');
    } finally {
      setSaving(false);
    }
  }

  if (!published) {
    return (
      <Box sx={{ p: 2, borderRadius: '10px', border: `1px dashed ${IPD_COLORS.border}` }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
          The vendor link becomes available once this RFQ is published.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 2, mb: 2, borderRadius: '10px',
      border: `1px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.cardBg,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
          Vendor link
        </Typography>
        <Chip
          size="small"
          label={openBid ? 'Open bid — new vendors can register' : 'Invited vendors only'}
          sx={{
            height: 20, fontSize: 11, fontWeight: 600,
            backgroundColor: openBid ? IPD_COLORS.success.bg : IPD_COLORS.selectedBg,
            color: openBid ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
          }}
        />
      </Box>

      {loading && <CircularProgress size={20} sx={{ color: IPD_COLORS.primary }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {shareUrl && (
        <>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            p: 1, borderRadius: '8px', backgroundColor: IPD_COLORS.selectedBg,
          }}>
            <Typography sx={{
              flex: 1, fontSize: 12, fontFamily: 'monospace',
              color: IPD_COLORS.textPrimary, wordBreak: 'break-all',
            }}>
              {shareUrl}
            </Typography>
            <Tooltip title={copied ? 'Copied' : 'Copy link'}>
              <IconButton size="small" onClick={copy} aria-label="Copy vendor link">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open as a vendor would see it">
              <IconButton
                size="small"
                component="a"
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open vendor link"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 1 }}>
            {openBid
              ? 'New vendors will see a registration card on this link, next to the existing-vendor login.'
              : 'Only invited vendors can quote. Turn on Open bid below if unregistered vendors should register here.'}
            {link.expires_at && ` Active until ${formatDateTime(link.expires_at)}.`}
          </Typography>
          {canToggleOpenBid && (
            <Box sx={{ mt: 1.5 }}>
              <InvButton
                size="small"
                variant={openBid ? 'outlined' : undefined}
                tone={openBid ? 'teal' : 'mint'}
                disabled={saving}
                onClick={() => setOpenBid(!openBid)}
              >
                {saving ? 'Saving…' : openBid ? 'Switch to invited vendors only' : 'Allow new vendors to register'}
              </InvButton>
            </Box>
          )}
        </>
      )}

      {!loading && !link && !error && (
        <InvButton size="small" variant="outlined" tone="teal" onClick={load}>
          Get vendor link
        </InvButton>
      )}
    </Box>
  );
}
